"use client";

import React, { useEffect, useState } from 'react';

type Centre = 'bruxelles' | 'brabant';
type Lang = 'fr' | 'nl';

// Paragraphe "Responsable / Verantwoordelijke" propre à chaque entité (adresse + numéro).
const RESP: Record<Centre, { fr: string; nl: string }> = {
  bruxelles: {
    fr: "Le responsable du traitement est l’asbl Centres Médicaux César de Paepe. L’asbl est enregistrée à la Banque carrefour des Entreprises sous le numéro 0446.977.681 et son siège social est situé à l’adresse Rue des Moineaux 17-19, 1000 Bruxelles",
    nl: "De verantwoordelijke voor de verwerking van de gegevens is de vzw Medische Centra César de Paepe. De vzw is geregistreerd bij de Kruispuntbank van Ondernemingen onder het nummer 0446.977.681 en haar maatschappelijke zetel bevindt zich in de Mussenstraat 17-19, 1000 Brussel.",
  },
  brabant: {
    fr: "Le responsable du traitement est l’asbl Centres Médicaux César de Paepe. L’asbl est enregistrée à la Banque carrefour des Entreprises sous le numéro 0410.684.241 et son siège social est situé à l’adresse Bogaardenstraat 25, 3200 Aarschot",
    nl: "De verantwoordelijke voor de verwerking van de gegevens is de vzw Medische Centra César de Paepe. De vzw is geregistreerd bij de Kruispuntbank van Ondernemingen onder het nummer 0410.684.241 en haar maatschappelijke zetel bevindt zich in de Bogaardenstraat 25, 3200 Aarschot.",
  },
};

const H2 = 'text-xl font-bold text-gray-900 mt-8 mb-3';
const H3 = 'font-bold text-gray-900 mt-5 mb-2';
const P = 'text-gray-600 mb-3 leading-relaxed';
const UL = 'text-gray-600 mb-4 list-disc pl-6 space-y-1';

function CharterFR({ responsable }: { responsable: string }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Charte vie privée des Centres Médicaux César De Paepe asbl</h2>

      <h3 className={H2}>I. Introduction</h3>
      <p className={P}>Les informations qui suivent concernent la politique mise en place par les Centres médicaux César de Paepe asbl pour protéger vos données personnelles.</p>
      <p className={P}>Cette politique a pour but de vous informer, de manière claire et transparente, sur la manière dont vos données sont collectées, utilisées et protégées conformément au Règlement Général sur la protection des données (RGPD) et à la législation belge applicable.</p>

      <h3 className={H2}>II. Responsable du traitement</h3>
      <p className={P}>{responsable}</p>

      <h3 className={H2}>III. Destinataire de cette Charte</h3>
      <p className={P}>La présente Charte s’applique automatiquement aux patients dans le cadre de leur prise en charge au sein des Centres Médicaux César de Paepe</p>

      <h3 className={H2}>IV. Données personnelles traitées</h3>
      <p className={P}>Dans le cadre de nos activités, nous traitons ou nous sommes susceptibles de traiter certaines données à caractère personnel. Il s’agit :</p>
      <ul className={UL}>
        <li>Des données d’identification : Nom, prénom, numéro de registre national</li>
        <li>Des données de contact : adresse postale, numéro de téléphone, ...</li>
        <li>Des données administratives et financières : données pour la facturation,...</li>
        <li>Des données physiques ;</li>
        <li>Des données numériques à caractère personnel : adresse e-mail,...</li>
        <li>Des données sensibles : informations médicales nécessaires à votre prise en charge</li>
      </ul>

      <h3 className={H2}>V. Sources des données traitées</h3>
      <p className={P}>Les données que nous traitons sont des données que vous nous fournissez directement ou des données nécessaires à la prise en charge de votre dossier médical.</p>

      <h3 className={H2}>VI. Finalités du traitement</h3>
      <p className={P}>Vos données personnelles sont traitées exclusivement pour les finalités non exhaustives suivantes :</p>
      <ul className={UL}>
        <li>Assurer votre prise en charge médicale et paramédicale</li>
        <li>Constituer et gérer votre dossier patient</li>
        <li>Assurer la continuité et la qualité des soins</li>
        <li>Répondre à nos obligations légales et réglementaires (INAMI, mutualités, obligations comptables)</li>
        <li>Gérer la facturation et les remboursements</li>
        <li>Communiquer avec vous dans le cadre de votre suivi médical</li>
        <li>Gestion des plaintes, de la médiation et du contentieux</li>
      </ul>

      <h3 className={H2}>VII. Base juridique du traitement de vos données</h3>
      <p className={P}>Nous traitons vos données conformément aux lois belges applicables et au RGPD notamment le chapitre II du RGPD. Le traitement des données vous concernant s’effectue :</p>
      <ul className={UL}>
        <li>Soit en application d’une législation qui s’impose à nous</li>
        <li>Soit pour préserver vos intérêts vitaux</li>
        <li>Soit dans l’intérêt légitime du Centre. Dans ces situations, nous veillons toujours à ce que ces traitements soient proportionnés et à ce que vos droits et libertés fondamentaux soient respectés.</li>
        <li>Soit sur base d’un consentement que vous avez donné</li>
      </ul>
      <p className={P}>En dehors de l’hypothèse où vous avez donné un consentement préalable, les législations justifiant le traitement de vos données à caractère personnel peuvent être, entre autres :</p>
      <ul className={UL}>
        <li>La loi du 30 juillet 2018 relative à la protection des personnes physiques à l’égard des traitements de données à caractère personnel</li>
        <li>La loi coordonnée du 10 juillet 2008 sur les hôpitaux et autres établissements de soins</li>
        <li>La loi du 22 avril 2019 relative à la qualité de la pratique des soins de santé</li>
        <li>La loi du 22 août 2002 relative aux droits du patient</li>
        <li>L’arrêté royal du 3 mai 1999 déterminant les conditions générales minimales auxquelles le dossier médical doit répondre</li>
        <li>La loi relative à l’assurance obligatoire soins de santé et indemnités coordonnée du 14 juillet 1994 et ses arrêtés d’exécution</li>
      </ul>

      <h3 className={H2}>VIII. Catégories de personnes ayant accès aux données</h3>
      <p className={P}>Vos données personnelles sont accessibles, quand cela est nécessaire, pour les catégories de personnes suivantes :</p>
      <ul className={UL}>
        <li>Vous ou vos représentants dans le respect de la loi du 22 août 2002 relative aux droits du patient</li>
        <li>Aux professionnels de santé du centre impliqué dans votre prise en charge</li>
        <li>Aux membres du personnel administratif dans la mesure du nécessaire</li>
        <li>Aux organismes légalement habilités (INAMI, organismes assureurs, SPF Santé et autres autorités compétentes, ...)</li>
        <li>A nos sous-traitants quand cela s’avère nécessaire et dans le cadre d’un contrat garantissant la confidentialité et la sécurité des données</li>
      </ul>
      <p className={P}>A l’exception de ces catégories, seules des données anonymisées ou codifiées sans possibilité de réidentification pourront être communiquées à d’autres personnes ou instances.</p>

      <h3 className={H2}>IX. Durée de conservation</h3>
      <p className={P}>Les Centres Médicaux César de Paepe conservent vos données pendant une période allant de 2 à 30 ans en fonction des obligations légales applicables à la gestion du dossier médical, de la réglementation en vigueur et/ou de leur nécessité dans l’exécution des activités des Centres.</p>

      <h3 className={H2}>X. Sécurité des données traitées</h3>
      <p className={P}>Les Centres Médicaux mettent en œuvre les mesures techniques et organisationnelles appropriées afin de garantir la sécurité, l’intégrité et la confidentialité de vos données personnelles et d’empêcher tout accès non-autorisé, perte ou divulgation.</p>

      <h3 className={H2}>XI. Vos droits</h3>
      <h4 className={H3}>Droits d’information et d’accès :</h4>
      <p className={P}>Vous pouvez toujours nous demander d’accéder à vos données à caractère personnel que nous conservons. Vous avez le droit d’être informé et de recevoir sans frais une copie précisant entre autres :</p>
      <ul className={UL}>
        <li>Quelles données à caractère personnel sont traitées ;</li>
        <li>Quelle est la provenance des données traitées ;</li>
        <li>Pourquoi elles sont traitées ;</li>
        <li>Avec quelles catégories de tiers nous partageons vos données ;</li>
      </ul>
      <p className={P}>La première copie est gratuite et les demandes ultérieures ayant le même objet vous seront facturées en application de l’article 12, §5 du RGPD</p>
      <h4 className={H3}>Droits de rectification :</h4>
      <p className={P}>Vous avez le droit de nous demander de corriger des informations personnelles inexactes ou incomplètes vous concernant.</p>
      <h4 className={H3}>Droit d’opposition :</h4>
      <p className={P}>Lorsque le traitement de vos données repose simplement sur les intérêts légitimes des Centres Médicaux et que vous n’êtes pas convaincu de la légitimité dudit fondement, vous disposez du droit de vous opposer à ce traitement en formant opposition par écrit. S’il s’avère après analyse que les Centres Médicaux disposent d’intérêts légitimes impérieux, il ne sera pas possible de s’y opposer.</p>
      <p className={P}>Vous pouvez également vous opposer à tout moment à l’utilisation de vos données à des fins de prospection, le cas échéant.</p>
      <h4 className={H3}>Droit à la limitation du traitement :</h4>
      <p className={P}>Dans certaines situations, vous pouvez demander une limitation du traitement de vos données. Cela a pour effet que nous ne pouvons plus traiter vos données à l’exception de la conservation de ces dernières</p>
      <h4 className={H3}>Droit d’effacement :</h4>
      <p className={P}>Lorsque vos données à caractère personnel ne sont pas pertinentes ou ne sont plus nécessaires aux fins du traitement, vous pouvez nous demander de les supprimer. Dans certaines circonstances, aucune suite ne pourra cependant être donnée à votre demande de suppression, notamment si vos données à caractère personnel peuvent ou doivent encore être régulièrement traitées selon la réglementation en vigueur. De même, vos données ne pourront pas être supprimées lorsque le traitement est réalisé en vertu d’une obligation légale ou d’un archivage dans l’intérêt général, pour la recherche scientifique ou historique ou à des fins statistiques et qu’une suppression empêche ou compromet ces finalités.</p>
      <h4 className={H3}>Droit d’introduire une réclamation :</h4>
      <p className={P}>Afin d’exercer vos droits ou pour poser toutes questions en lien avec la protection de vos données, veuillez contacter le Délégué à la protection des données à l’adresse électronique suivante : <a href="mailto:dataprotection.bra@solidaris.be" className="text-primary hover:underline">dataprotection.bra@solidaris.be</a> ou à l’adresse postale suivante : 111 Rue du Midi, 1000 Bruxelles en mentionnant que le courrier est à destination du Délégué à la protection des données.</p>
      <p className={P}>Si vous estimez que la solution apportée par le Délégué à la protection des données n’est pas satisfaisante, vous pouvez introduire une réclamation à l’Autorité de protection des données à l’adresse postale suivante : Rue de la Presse, 35, 1000 Bruxelles ou via leur adresse électronique : <a href="mailto:contact@apd-gba.be" className="text-primary hover:underline">contact@apd-gba.be</a></p>

      <h3 className={H2}>XII. Modification de la charte</h3>
      <p className={P}>La présente charte est en vigueur à la date mentionnée en fin de page et sera modifiée afin de tenir compte des évolutions légales et organisationnelles.</p>

      <p className="text-gray-400 text-sm mt-8">01/06/2026</p>
    </div>
  );
}

function CharterNL({ verantwoordelijke }: { verantwoordelijke: string }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacycharter van de vzw Medische Centra César De Paepe vzw</h2>

      <h3 className={H2}>I. Inleiding</h3>
      <p className={P}>De volgende informatie heeft betrekking op het beleid dat de Medische Centra César de Paepe vzw voert om je persoonsgegevens te beschermen.</p>
      <p className={P}>Het doel van dit charter is om je op een duidelijke en transparante manier te informeren over hoe je gegevens worden verzameld, gebruikt en beschermd in overeenstemming met de Algemene Verordening Gegevensbescherming (ook bekend onder de Engelse term ‘General Data Protection Regulation’) en de toepasselijke Belgische wetgeving.</p>

      <h3 className={H2}>II. Wie is verantwoordelijk voor de verwerking?</h3>
      <p className={P}>{verantwoordelijke}</p>

      <h3 className={H2}>III. Voor wie is dit beleid bestemd?</h3>
      <p className={P}>Dit charter is automatisch van toepassing op patiënten in het kader van hun behandeling in de Medische Centra César de Paepe.</p>

      <h3 className={H2}>IV. Welke persoonsgegevens worden verwerkt?</h3>
      <p className={P}>In het kader van onze activiteiten verwerken we bepaalde persoonsgegevens of kunnen we deze verwerken. Het gaat om:</p>
      <ul className={UL}>
        <li>identificatiegegevens: naam, voornaam, rijksregisternummer</li>
        <li>contactgegevens: postadres, telefoonnummer, ...</li>
        <li>administratieve en financiële gegevens: facturatiegegevens, ...</li>
        <li>fysieke gegevens;</li>
        <li>digitale persoonsgegevens: e-mailadres, ...</li>
        <li>gevoelige gegevens: medische informatie die nodig is voor je behandeling</li>
      </ul>

      <h3 className={H2}>V. Welke gegevensbronnen worden verwerkt?</h3>
      <p className={P}>De gegevens die wij verwerken zijn gegevens die je rechtstreeks aan ons verstrekt of gegevens die noodzakelijk zijn voor het beheer van je medische dossier.</p>

      <h3 className={H2}>VI. Wat zijn de doeleinden van de verwerking?</h3>
      <p className={P}>Je persoonsgegevens worden uitsluitend verwerkt voor de volgende niet-limitatieve doeleinden:</p>
      <ul className={UL}>
        <li>je medische en paramedische behandeling garanderen;</li>
        <li>je patiëntendossier aanmaken en beheren;</li>
        <li>de continuïteit en de kwaliteit van de zorg waarborgen;</li>
        <li>voldoen aan onze wettelijke en reglementaire verplichtingen (RIZIV, ziekenfondsen, boekhoudkundige verplichtingen);</li>
        <li>de facturatie en de terugbetalingen beheren;</li>
        <li>met jou communiceren in het kader van je medische opvolging;</li>
        <li>het beheer van klachten, bemiddeling en geschillen</li>
      </ul>

      <h3 className={H2}>VII. Wat is de rechtsgrondslag voor de verwerking van je gegevens?</h3>
      <p className={P}>Wij verwerken je gegevens in overeenstemming met de toepasselijke Belgische wetgeving en de AVG, in het bijzonder hoofdstuk II van de AVG. Je gegevens worden verwerkt:</p>
      <ul className={UL}>
        <li>hetzij in toepassing van een wetgeving die bindend is voor ons;</li>
        <li>hetzij om je vitale belangen te beschermen;</li>
        <li>hetzij in het gerechtvaardigde belang van het Centrum. In deze situaties zorgen we er altijd voor dat dergelijke verwerkingen evenredig zijn en dat jouw fundamentele rechten en vrijheden worden gerespecteerd;</li>
        <li>hetzij op basis van een door jou gegeven toestemming</li>
      </ul>
      <p className={P}>Behalve indien je vooraf toestemming hebt gegeven, kan de regel- en wetgeving die de verwerking van je persoonsgegevens rechtvaardigt onder meer de volgende zijn:</p>
      <ul className={UL}>
        <li>de wet van 30 juli 2018 betreffende de bescherming van natuurlijke personen met betrekking tot de verwerking van persoonsgegevens;</li>
        <li>de gecoördineerde wet van 10 juli 2008 op de ziekenhuizen en andere verzorgingsinrichtingen;</li>
        <li>de wet van 22 april 2019 betreffende de kwaliteit van de gezondheidszorgpraktijk;</li>
        <li>de wet van 22 augustus 2002 betreffende de rechten van de patiënt;</li>
        <li>het koninklijk besluit van 3 mei 1999 houdende bepaling van de algemene minimumvoorwaarden waaraan het medische dossier moet voldoen;</li>
        <li>de wet betreffende de verplichte verzekering voor geneeskundige verzorging en uitkeringen gecoördineerd op 14 juli 1994 en de uitvoeringsbesluiten ervan</li>
      </ul>

      <h3 className={H2}>VIII. Welke categorieën van personen hebben toegang tot de gegevens?</h3>
      <p className={P}>Je persoonsgegevens zijn, indien nodig, toegankelijk voor de volgende categorieën van personen:</p>
      <ul className={UL}>
        <li>jij of je vertegenwoordigers in overeenstemming met de wet van 22 augustus 2002 betreffende de rechten van de patiënt;</li>
        <li>de zorgverleners van het centrum die betrokken zijn bij je behandeling;</li>
        <li>het administratief personeel, waar nodig;</li>
        <li>de wettelijk bevoegde instanties (RIZIV, verzekeringsinstellingen, FOD Volksgezondheid en andere bevoegde instanties, enz.);</li>
        <li>onze verwerkers, indien nodig, in het kader van een contract dat de vertrouwelijkheid en veiligheid van de gegevens garandeert.</li>
      </ul>
      <p className={P}>Met uitzondering van deze categorieën mogen enkel geanonimiseerde of gecodificeerde gegevens die niet opnieuw kunnen worden geïdentificeerd, aan andere personen of instanties worden meegedeeld.</p>

      <h3 className={H2}>IX. Hoe lang worden de gegevens bewaard?</h3>
      <p className={P}>De Medische Centra César de Paepe bewaren je gegevens gedurende een periode van 2 tot 30 jaar, afhankelijk van de wettelijke verplichtingen die van toepassing zijn op het beheer van het medische dossier, de geldende regelgeving en/of de noodzaak ervan voor de uitvoering van de activiteiten van de centra.</p>

      <h3 className={H2}>X. Hoe worden de verwerkte gegevens beveiligd?</h3>
      <p className={P}>De Medische Centra treffen passende technische en organisatorische maatregelen om de veiligheid, integriteit en vertrouwelijkheid van je persoonsgegevens te waarborgen en ongeoorloofde toegang, verlies of openbaarmaking te voorkomen.</p>

      <h3 className={H2}>XI. Welke rechten heb je?</h3>
      <h4 className={H3}>Recht op informatie en van inzage:</h4>
      <p className={P}>je kan ons altijd vragen om toegang te krijgen tot de persoonsgegevens die wij bewaren. Je hebt recht op inzage en om kosteloos een kopie te ontvangen waarin onder meer het volgende wordt toegelicht:</p>
      <ul className={UL}>
        <li>welke persoonsgegevens worden verwerkt;</li>
        <li>waar komen de verwerkte gegevens vandaan;</li>
        <li>waarom worden ze verwerkt;</li>
        <li>met welke categorieën van derden delen wij jouw gegevens</li>
      </ul>
      <p className={P}>De eerste kopie is gratis. Latere verzoeken over hetzelfde onderwerp zullen worden aangerekend in overeenstemming met artikel 12, § 5 van de AVG.</p>
      <h4 className={H3}>Recht op verbetering:</h4>
      <p className={P}>Indien bepaalde persoonsgegevens volgens jou onjuist of onvolledig zijn, kan je ons vragen deze te corrigeren.</p>
      <h4 className={H3}>Recht van bezwaar:</h4>
      <p className={P}>Wanneer de verwerking van je gegevens uitsluitend is gebaseerd op de gerechtvaardigde belangen van de Medische Centra en je niet overtuigd bent van de legitimiteit van de genoemde grondslag, heb je het recht om schriftelijk bezwaar te maken tegen deze verwerking. Als na analyse blijkt dat de Medische Centra doorslaggevende gerechtvaardigde belangen hebben, is het niet mogelijk om bezwaar te maken.</p>
      <p className={P}>Je kan ook te allen tijde bezwaar maken tegen het gebruik van je gegevens voor direct marketingdoeleinden.</p>
      <h4 className={H3}>Recht op beperking van de verwerking:</h4>
      <p className={P}>In bepaalde situaties kan je vragen om de verwerking van je gegevens te beperken. Hierdoor kunnen we je gegevens niet langer verwerken, met uitzondering van de bewaring ervan.</p>
      <h4 className={H3}>Recht op gegevenswissing:</h4>
      <p className={P}>Als je persoonsgegevens niet langer relevant of noodzakelijk zijn voor de doeleinden van de verwerking, kan je ons vragen om ze te verwijderen. In sommige gevallen kan echter geen gevolg worden gegeven aan je verzoek tot verwijdering, met name als je persoonsgegevens nog regelmatig kunnen of moeten worden verwerkt in overeenstemming met de geldende regelgeving. Je gegevens mogen ook niet worden gewist wanneer de verwerking wordt uitgevoerd op grond van een wettelijke verplichting of voor archivering in het algemeen belang, voor wetenschappelijk of historisch onderzoek of voor statistische doeleinden en wanneer het wissen deze doeleinden zou verhinderen of in gevaar brengen.</p>
      <h4 className={H3}>Recht om een klacht in te dienen:</h4>
      <p className={P}>Om je rechten uit te oefenen of vragen te stellen over de bescherming van je gegevens, kan je contact opnemen met de functionaris voor gegevensbescherming door een e-mail te sturen naar <a href="mailto:dataprotection.bra@solidaris.be" className="text-primary hover:underline">dataprotection.bra@solidaris.be</a> of een brief te verzenden naar Zuidstraat 111, 1000 Brussel, met de vermelding dat de brief aan de functionaris voor gegevensbescherming is gericht.</p>
      <p className={P}>Als je van mening bent dat de oplossing van de functionaris voor gegevensbescherming niet afdoend is, kan je een klacht indienen bij de Gegevensbeschermingsautoriteit door een brief te sturen naar Drukpersstraat 35 of een e-mail te verzenden naar <a href="mailto:contact@apd-gba.be" className="text-primary hover:underline">contact@apd-gba.be</a>.</p>

      <h3 className={H2}>XII. Kan het charter worden gewijzigd?</h3>
      <p className={P}>Dit charter treedt in werking op de datum die aan het einde van de pagina wordt vermeld en zal worden gewijzigd om rekening te houden met juridische en organisatorische ontwikkelingen</p>

      <p className="text-gray-400 text-sm mt-8">01/06/2026</p>
    </div>
  );
}

export default function PolitiqueViePrivee() {
  const [centre, setCentre] = useState<Centre>('bruxelles');
  const [lang, setLang] = useState<Lang>('fr');

  // Aligne la langue par défaut sur la langue Weglot active (NL -> nl, sinon fr).
  useEffect(() => {
    try {
      const w = (window as any).Weglot;
      const current = w && typeof w.getCurrentLang === 'function' ? w.getCurrentLang() : null;
      if (current === 'nl') setLang('nl');
    } catch {
      /* noop */
    }
  }, []);

  const centres: { key: Centre; label: string }[] = [
    { key: 'bruxelles', label: 'Bruxelles' },
    { key: 'brabant', label: 'Brabant flamand' },
  ];
  const langs: { key: Lang; label: string }[] = [
    { key: 'fr', label: 'Français' },
    { key: 'nl', label: 'Nederlands' },
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row gap-8 lg:gap-12">

          {/* Sélecteurs — sidebar (desktop) / haut (mobile) */}
          <aside className="md:w-64 flex-shrink-0">
            <div className="md:sticky md:top-28 space-y-6">
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2.5" style={{ fontFamily: 'Poppins, sans-serif' }}>Centre</p>
                <div className="flex md:flex-col gap-2">
                  {centres.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setCentre(c.key)}
                      className={`flex-1 md:w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer border ${
                        centre === c.key
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-white text-text-main border-gray-200 hover:border-primary/40'
                      }`}
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                      aria-pressed={centre === c.key}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2.5" style={{ fontFamily: 'Poppins, sans-serif' }}>Langue</p>
                <div className="flex md:flex-col gap-2">
                  {langs.map((l) => (
                    <button
                      key={l.key}
                      onClick={() => setLang(l.key)}
                      className={`flex-1 md:w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer border ${
                        lang === l.key
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'bg-white text-text-main border-gray-200 hover:border-primary/40'
                      }`}
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                      aria-pressed={lang === l.key}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Contenu — exclu de la traduction automatique Weglot */}
          <div
            className="flex-1 min-w-0 max-w-3xl wg-notranslate notranslate"
            translate="no"
            data-wg-notranslate
            lang={lang}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {lang === 'fr'
              ? <CharterFR responsable={RESP[centre].fr} />
              : <CharterNL verantwoordelijke={RESP[centre].nl} />}
          </div>
        </div>
      </div>
    </section>
  );
}
